import React,{ Component } from 'react'

import formatDate from "../../utils/formatDate";
import PropTypes from 'prop-types'

import { orderBy } from "lodash-es";

import Highlighter from "./Highlighter";
import Pagination from "./Pagination";
import ColumnFilterPopover from "./ColumnFilterPopover";
import {
	applyColumnFilters,
	deriveFilterOptions,
	hasActiveColumnFilters,
} from "../../utils/tableFilters";

export default class SortableTable extends Component{
	constructor(props){
		super(props)

		this.paginationRef = React.createRef()

		this.state = {
			view:        props.view ? props.view : 100,
			start:       1,
			end:         props.all ? props.info.length : props.view ? props.view : 100,
			// Use external sort props if provided (for server-side sorting), otherwise default to empty
			sort:        props.sortBy || '',
			sortBy:      props.sortDirection || 'asc',
			selected:    [],
			selectedAll: false,
			colTotals:   null,
			filters:     props.filters || {},
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.filters !== this.props.filters && this.props.filters !== undefined) {
			this.setState({ filters: this.props.filters || {} })
		}
	}

	changeSort(variable) {
		// If external sort handler is provided, use it
		if (this.props.onSort) {
			this.props.onSort(variable);
			return;
		}
		
		// Otherwise, use internal sorting
		const newSortBy = this.state.sortBy === 'desc' ? 'asc' : 'desc'
		if ( this.state.sort === variable ) {
			this.setState({
				sortBy: newSortBy
			})
		} else {
			this.setState({
				sort: variable,
				sortBy: 'desc'
			})
		}
	}

	sortDirection(variable) {
		// Use external sort state if provided, otherwise use internal state
		const currentSort = this.props.sortBy !== undefined ? this.props.sortBy : this.state.sort;
		const currentDirection = this.props.sortDirection !== undefined ? this.props.sortDirection : this.state.sortBy;
		
		if ( currentSort === variable ) {
			return currentDirection === 'desc' ? <span className={'fas fa-sort-down'}/> : <span className={'fas fa-sort-up'}/>
		} else {
			return <span className={'fas fa-sort'}/>
		}
	}

	getFilterField = (header) => {
		return header.filterField || (Array.isArray(header.value) ? header.value[0] : header.value)
	}

	isFilterable = (header) => {
		return header.filter !== false && header.name && header.name.length > 0
	}

	getRowFilterValue = (header, row) => {
		if (typeof header.filterValue === 'function') {
			return header.filterValue(row)
		}
		const field = this.getFilterField(header)
		if (header.date) {
			const raw = row[field]
			if (!raw) {
				return null
			}
			const date = new Date(raw)
			if (Number.isNaN(date.getTime())) {
				return String(raw).slice(0, 10)
			}
			return date.toISOString().slice(0, 10)
		}
		return row[field]
	}

	getRowFilterLabel = (header, row) => {
		if (typeof header.filterLabel === 'function') {
			return header.filterLabel(row)
		}
		if (header.date) {
			const raw = row[this.getFilterField(header)]
			return raw ? formatDate(raw) : '(Blank)'
		}
		const value = this.getRowFilterValue(header, row)
		if (value === null || value === undefined || value === '') {
			return '(Blank)'
		}
		return String(value)
	}

	getClientFilterOptions = () => {
		const options = {}
		;(this.props.headers || []).forEach((header) => {
			if (!this.isFilterable(header)) {
				return
			}
			const field = this.getFilterField(header)
			options[field] = deriveFilterOptions(
				this.props.info,
				(row) => this.getRowFilterValue(header, row),
				(row) => this.getRowFilterLabel(header, row)
			)
		})
		return options
	}

	getActiveFilters = () => {
		return this.props.filters !== undefined ? (this.props.filters || {}) : this.state.filters
	}

	handleFiltersChange = (nextFiltersOrUpdater) => {
		if (this.props.onFiltersChange) {
			this.props.onFiltersChange(nextFiltersOrUpdater)
			return
		}
		this.setState((prev) => {
			const next =
				typeof nextFiltersOrUpdater === 'function'
					? nextFiltersOrUpdater(prev.filters || {})
					: nextFiltersOrUpdater || {}
			return { filters: next }
		}, () => {
			if (this.paginationRef.current && this.paginationRef.current.resetPage) {
				this.paginationRef.current.resetPage()
			}
		})
	}

	getFilterOptionsForHeader = (header, clientOptions) => {
		const field = this.getFilterField(header)
		if (this.props.serverFiltering && this.props.filterOptions) {
			return this.props.filterOptions[field] || []
		}
		return clientOptions[field] || []
	}

	getProcessedData = () => {
		const serverMode = !!this.props.onSort
		const filters = this.getActiveFilters()
		let rows = this.props.info || []

		if (!this.props.serverFiltering) {
			const getValueByField = {}
			;(this.props.headers || []).forEach((header) => {
				if (!this.isFilterable(header)) {
					return
				}
				const field = this.getFilterField(header)
				getValueByField[field] = (row) => this.getRowFilterValue(header, row)
			})
			rows = applyColumnFilters(rows, filters, getValueByField)
		}

		if (serverMode) {
			return rows
		}

		const sortField = this.state.sort
		const sortDirection = this.state.sortBy
		if (!sortField) {
			return rows
		}

		const sortHeader = (this.props.headers || []).find((header) => {
			const field = Array.isArray(header.value) ? header.value[0] : header.value
			return field === sortField || header.filterField === sortField
		})

		if (sortHeader && typeof sortHeader.sortValue === 'function') {
			return orderBy(rows, [(row) => sortHeader.sortValue(row)], [sortDirection])
		}

		return orderBy(
			rows,
			Array.isArray(sortField) ? sortField : [sortField],
			[sortDirection]
		)
	}

	multiRow(values,row) {
		return values.map((val, key) => (
			<div key={'multiRow'+key+(row[this.props.id?this.props.id:Math.random()])}>
				{key>0?<hr style={{margin:'10px'}}/>:''}
					<Highlighter check={row[val]}>
						<span style={key>0?{color:'#8f8f8f'}:{}}>{row[val]?row[val]:'None'}</span>
					</Highlighter>
			</div>
		))
	}

	renderRow = (row) => {

		let rowProps = {};
		if( typeof this.props.rowProps === "function") {
			rowProps = this.props.rowProps(row);
		} else if (this.props.rowProps) {
			rowProps = this.props.rowProps
		}

		return <tr key={this.props.id ? row[this.props.id] : Math.random()} {...rowProps}>
			{this.props.headers.map((header)=> (
				<td key={'tableData'+(header.name)+(this.props.id?row[this.props.id]:Math.random())} width={header.width ? header.width+'%' : 'auto'}>
					{header.custom ?
						header.custom(Array.isArray(header.value)?header.value.map(val=>{return row[val]}):header.value === true ? row : row[header.value])
						:
						header.date ?
							formatDate(row[header.value])
							:
							Array.isArray(header.value) ?
								this.multiRow(header.value, row)
								:
								<Highlighter check={row[header.value]}><span>{row[header.value]}</span></Highlighter>
					}
				</td>
			))}
			{this.props.rowTotals &&
				<td>
					{this.props.headers.map(header=>(
						!header.noTotal && row[header.value]
					)).reduce((partial_sum, a) =>
						partial_sum + a
					)}
				</td>
			}
		</tr>
	}

	renderHeaderCell = (header, clientOptions) => {
		const field = this.getFilterField(header)
		const filterable = this.isFilterable(header)
		const sortable = header.sort !== false && header.name.length > 0
		const filterOptions = filterable ? this.getFilterOptionsForHeader(header, clientOptions) : []

		if (!header.name || header.name.length === 0) {
			return <th key={field || header.value}>{header.name}</th>
		}

		return (
			<th
				key={field || header.value}
				style={{whiteSpace:'nowrap'}}
			>
				<span className="d-inline-flex align-items-center">
					{sortable ? (
						<span
							className="pointer"
							onClick={()=>{this.changeSort(header.value)}}
							style={{whiteSpace:'nowrap'}}
						>
							{header.name} {this.sortDirection(header.value)}
						</span>
					) : (
						<span>{header.name}</span>
					)}
					{filterable && (
						<ColumnFilterPopover
							field={field}
							options={filterOptions}
							filters={this.getActiveFilters()}
							onFiltersChange={this.handleFiltersChange}
						/>
					)}
				</span>
			</th>
		)
	}

	render(){
		const clientOptions = this.props.serverFiltering ? {} : this.getClientFilterOptions()
		const orderedData = this.getProcessedData()
		const filters = this.getActiveFilters()
		const hasFilters = hasActiveColumnFilters(filters)

		if ((this.props.info || []).length === 0 && !hasFilters) {
			return '';
		}

		let grandTotal = 0
		const colTotals = this.props.colTotals &&
			<tr style={{backgroundColor: "#fbfbfb"}}>
				{this.props.headers.map((header,key)=>{
					return <td key={header.value}>
						{header.noTotal ?
							key === 0 ?
								'Totals'
								:
								'None'
							:
							this.props.info.map(row => (
								parseInt(row[header.value])
							)).reduce((partial_sum, a, index) => {
								grandTotal = grandTotal + (index === 1 ? partial_sum + a : a)
								return partial_sum + a
							})
						}
					</td>
				})}
				{this.props.rowTotals &&
					<td>{grandTotal}</td>
				}
			</tr>

		return (
			<div className="table-responsive">
				<table
					className={this.props.className ? this.props.className:'table table-hover'}
				>
					<thead>
						<tr>
							{this.props.checkbox &&
								<th>
									<input
									type={'checkbox'}
									className={'checkbox'}
									checked={this.state.selectedAll || this.state.selected.length === this.props.info.length}
									onChange={()=>{this.setState(prevState=>{return {selectedAll:!prevState.selectedAll,selected:[]}})}}
									/>
								</th>
							}
							{this.props.headers.map((header) => this.renderHeaderCell(header, clientOptions))}
							{this.props.rowTotals && <th>Total</th>}
						</tr>
					</thead>

					<tbody>
						{this.props.all ?
							orderedData.map(row=>this.renderRow(row))
							:
							<Pagination
								onRef={(ref) => { this.paginationRef.current = ref }}
								info={orderedData}
								render={this.renderRow}
								renderAfter={colTotals}
								view={this.state.view}
								table
							/>
						}
					</tbody>
				</table>
				{orderedData.length === 0 && hasFilters ? (
					<div className="m-3 p-3 border rounded bg-light">No jobs match the selected filters</div>
				) : null}
			</div>
		)
	}
}

SortableTable.propTypes = {
	headers: PropTypes.array.isRequired,
	info: PropTypes.array.isRequired,
	view: PropTypes.number,
	all: PropTypes.bool,
	onSort: PropTypes.func,
	sortBy: PropTypes.string,
	sortDirection: PropTypes.string,
	filters: PropTypes.object,
	filterOptions: PropTypes.object,
	onFiltersChange: PropTypes.func,
	serverFiltering: PropTypes.bool,
}
